import { useEffect, useRef, useState } from "react";
import quotes from "../data/participant-quotes.json";

// Kept with the component so the same styles apply inside the mirror's shadow root.
export const QUOTE_DURATION_MS = 8000;

const styles = `
.participant-quotes { background:#080d16; color:#fff; font-family:Poppins,sans-serif; padding:48px 0 28px; }
.participant-quotes * { box-sizing:border-box; }
.participant-quotes h2 { margin:0 24px 28px; color:#fff; text-align:center; font-size:clamp(24px,3vw,38px); font-weight:500; line-height:1.3; }
.participant-quotes__stage { display:grid; overflow:hidden; touch-action:pan-y; }
.participant-quotes__slide { grid-area:1/1; position:relative; display:flex; align-items:center; min-height:470px; margin:0; padding:60px max(7vw,24px); isolation:isolate; visibility:hidden; opacity:0; transition:opacity .3s ease; }
.participant-quotes__slide[data-active="true"] { visibility:visible; opacity:1; }
.participant-quotes__slide img { position:absolute; inset:0; z-index:-2; width:100%; height:100%; object-fit:cover; object-position:center; }
.participant-quotes__slide::before { content:""; position:absolute; inset:0; z-index:-1; background:linear-gradient(90deg,rgba(3,8,17,.87),rgba(3,8,17,.67) 60%,rgba(3,8,17,.27)); }
.participant-quotes__copy { max-width:860px; }
.participant-quotes__mark { display:block; height:54px; color:#5dd6e5; font-size:88px; line-height:1; }
.participant-quotes blockquote { margin:12px 0 24px; border:0; padding:0; color:#fff; font-size:clamp(25px,3.3vw,46px); font-weight:500; line-height:1.35; letter-spacing:-.025em; text-shadow:0 2px 18px #0006; }
.participant-quotes figcaption { color:#fff; font-size:14px; line-height:1.7; }
.participant-quotes figcaption span { display:block; color:#bdebf1; font-size:12px; }
.participant-quotes__controls { display:flex; justify-content:center; align-items:center; gap:12px; margin-top:20px; }
.participant-quotes button { display:inline-flex; justify-content:center; align-items:center; width:44px; height:44px; padding:0; border:1px solid #ffffff55; border-radius:50%; background:transparent; color:#fff; font:24px Poppins,sans-serif; cursor:pointer; }
.participant-quotes button svg { display:block; width:22px; height:22px; flex:none; }
.participant-quotes__progress { height:3px; background:#ffffff22; width:min(280px,calc(100% - 48px)); margin:16px auto 0; overflow:hidden; border-radius:3px; }
.participant-quotes__progress span { display:block; width:100%; height:100%; background:#5dd6e5; transform-origin:left; }
.participant-quotes button:hover { background:#ffffff18; }
.participant-quotes button:focus-visible { outline:3px solid #5dd6e5; outline-offset:3px; }
.participant-quotes button.participant-quotes__dot { width:32px; height:44px; border:0; border-radius:6px; }
.participant-quotes__dot::after { content:""; width:8px; height:8px; background:#ffffff66; border-radius:50%; }
.participant-quotes__dot[aria-current="true"]::after { background:#5dd6e5; width:22px; border-radius:8px; }
@media(max-width:600px) { .participant-quotes { padding-top:32px; } .participant-quotes__slide { min-height:480px; padding:40px 24px; } .participant-quotes__slide::before { background:linear-gradient(180deg,#03081166,#030811d9); } .participant-quotes blockquote { font-size:28px; } }
@media(prefers-reduced-motion:reduce) { .participant-quotes__slide { transition:none; } }
`;

export default function ParticipantQuotes() {
  const [active, setActive] = useState(0);
  const touch = useRef(null);
  const sectionRef = useRef(null);
  const elapsed = useRef(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(() => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
  const [visible, setVisible] = useState(true);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const running = !paused && visible && pageVisible;
  const select = (index) => {
    elapsed.current = 0;
    setProgress(0);
    setActive(index);
  };
  const move = (delta) => select((active + delta + quotes.length) % quotes.length);

  useEffect(() => {
    const updateVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", updateVisibility);
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting), { threshold: 0.15 },
    );
    observer?.observe(sectionRef.current);
    return () => {
      document.removeEventListener("visibilitychange", updateVisibility);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    let last = Date.now();
    const timer = setInterval(() => {
      const now = Date.now();
      elapsed.current += now - last;
      last = now;
      if (elapsed.current >= QUOTE_DURATION_MS) {
        elapsed.current = 0;
        setActive((current) => (current + 1) % quotes.length);
      }
      setProgress(elapsed.current / QUOTE_DURATION_MS);
    }, 100);
    return () => clearInterval(timer);
  }, [running]);
  return (
    <section ref={sectionRef} className="participant-quotes" aria-label="In their own words" aria-roledescription="carousel"
      onKeyDown={(event) => {
        if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
          event.preventDefault();
          move(event.key === "ArrowRight" ? 1 : -1);
        }
      }}>
      <style>{styles}</style>
      <h2>In their own words</h2>
      <div className="participant-quotes__stage" aria-live={running ? "off" : "polite"} aria-atomic="true"
        onTouchStart={(event) => { const point = event.touches[0]; touch.current = { x: point.clientX, y: point.clientY }; }}
        onTouchCancel={() => { touch.current = null; }}
        onTouchEnd={(event) => {
          if (!touch.current) return;
          const point = event.changedTouches[0];
          const dx = point.clientX - touch.current.x;
          const dy = point.clientY - touch.current.y;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) move(dx < 0 ? 1 : -1);
          touch.current = null;
        }}>
        {quotes.map((quote, index) => (
          <figure key={quote.id} className="participant-quotes__slide" data-active={index === active}
            aria-hidden={index !== active} aria-label={`${index + 1} of ${quotes.length}`} aria-roledescription="slide">
            <img src={quote.image} alt="" loading="lazy" decoding="async" />
            <div className="participant-quotes__copy">
              <span className="participant-quotes__mark" aria-hidden="true">“</span>
              <blockquote>{quote.text}</blockquote>
              <figcaption>{quote.experience}<span>{quote.context}</span></figcaption>
            </div>
          </figure>
        ))}
      </div>
      <div className="participant-quotes__controls">
        <button type="button" aria-label="Previous quote" onClick={() => move(-1)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M20 12H4m7-7-7 7 7 7" /></svg></button>
        {quotes.map((quote, index) => <button type="button" key={quote.id} className="participant-quotes__dot"
          aria-label={`Show quote ${index + 1}`} aria-current={index === active ? "true" : undefined} onClick={() => select(index)} />)}
        <button type="button" aria-label="Next quote" onClick={() => move(1)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M4 12h16m-7-7 7 7-7 7" /></svg></button>
        <button type="button" aria-label={paused ? "Play slideshow" : "Pause slideshow"} onClick={() => setPaused((value) => !value)}>
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">{paused ? <path d="m8 5 11 7-11 7z" /> : <path d="M6 5h4v14H6zm8 0h4v14h-4z" />}</svg>
        </button>
      </div>
      <div className="participant-quotes__progress" aria-hidden="true"><span style={{ transform: `scaleX(${progress})` }} /></div>
    </section>
  );
}
